import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  Chip, 
  Button,
  Grid,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { hospitalApi, Hospital } from '../../api/hospitalApi';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { itemApi } from '../../features/settings/items/api/itemApi';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

type OrderStatus = 'ordered' | 'billed' | 'paid' | 'partially_paid' | 'refunded' | 'cancelled';

const FINANCIAL_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ORDERED', label: 'Ordered' },
  { value: 'BILLED', label: 'Billed' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

interface FinancialOrder {
  id: string;
  appointmentNumber: string;
  patientName: string;
  serviceName: string;
  amount: number;
  paidAmount: number;
  pendingAmount: number;
  orderDate: string;
  status: OrderStatus;
  invoiceNumber?: string;
  doctorId: string;
  doctorName?: string;
  appointment?: any;
  item?: any;
}

const FinancialReport: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const reportRef = useRef<HTMLDivElement>(null);
  // Initialize dates in local timezone to avoid timezone conversion issues
  const initializeDefaultDates = () => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return { startOfDay, endOfDay };
  };
  
  const { startOfDay: defaultFromDate, endOfDay: defaultToDate } = initializeDefaultDates();
  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [doctorFilter, setDoctorFilter] = useState<string>('all');

  // Doctors list
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    import('../../features/employees/api/employeeApi').then(({ employeeApi }) => {
      employeeApi.getDoctors().then((data) => {
        setDoctors(data.map(doc => ({ id: doc.id, name: `${doc.firstName} ${doc.lastName}`.trim() }))); 
      });
    });
  }, []);

  // Items list for item filter
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [selectedItem, setSelectedItem] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    itemApi.getItems({ page: 1, limit: 100 }).then((resp) => {
      setItems(resp.data.map((item: any) => ({ id: item.id, name: item.itemName })));
    });
  }, []);

  // Patients list for patient filter
  const [patients, setPatients] = useState<{ id: string; name: string }[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    import('../../features/patients/api/patientApi').then(({ patientApi }) => {
      patientApi.getPatients({ page: 1, limit: 100 }).then((resp) => {
        setPatients(resp.data.map((p: any) => ({ id: p.id, name: p.fullName }))); 
      });
    });
  }, []);

  // Orders
  const [orders, setOrders] = useState<FinancialOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<any>(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  const [hospitalInfo, setHospitalInfo] = useState<Hospital | null>(null);

  const fetchOrders = () => {
    // Always send status in uppercase (except 'all')
    const statusParam = statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined;
    if (!fromDate || !toDate) return;
    setLoading(true);
    import('../../api/ReportApi').then(({ ReportApi }) => {
      // Format dates in local timezone to avoid timezone conversion issues
      const formatDateForAPI = (date: Date) => {
        // Use local timezone methods to avoid UTC conversion
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        // Return ISO-like format but in local timezone
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      console.log('--- FINANCIAL REPORT API CALL ---');
      console.log('From Date:', fromDate ? formatDateForAPI(fromDate) : undefined);
      console.log('To Date:', toDate ? formatDateForAPI(toDate) : undefined);
      console.log('Status Filter:', statusParam);
      console.log('Doctor Filter:', doctorFilter);
      console.log('Patient Filter:', selectedPatient);
      console.log('Item Filter:', selectedItem);
      
      ReportApi.getFinancialOrdersAdvanced({
        fromDate: fromDate ? formatDateForAPI(fromDate) : undefined,
        toDate: toDate ? formatDateForAPI(toDate) : undefined,
        status: statusParam,
        doctorId: doctorFilter !== 'all' ? doctorFilter : undefined,
        patientId: selectedPatient ? selectedPatient.id : undefined,
        itemId: selectedItem ? selectedItem.id : undefined,
      })
        .then((data: any[]) => {
          console.log('--- RAW API RESPONSE ---');
          console.log('Data length:', data.length);
          console.log('Raw data:', data);
          
          const mappedOrders = data.map(order => {
            const mapped = {
              id: order.id || order.appointment?.appointmentNumber || '',
              patientName: order.appointment?.patient?.fullName || '',
              serviceName: order.item?.itemName || order.serviceName || '',
              amount: order.grandTotal ?? 0,
              paidAmount: order.paidAmount ?? 0,
              pendingAmount: order.pendingAmount ?? 0,
              orderDate: order.orderDate || order.createdAt || order.createdDate,
              status: (order.financialStatus || '').toLowerCase() as OrderStatus,
              appointmentNumber: order.appointment?.appointmentNumber || order.appointmentNumber || order.orderNumber || order.id,
              doctorId: order.appointment?.doctor?.id || '',
              doctorName: [order.appointment?.doctor?.firstName, order.appointment?.doctor?.lastName].filter(Boolean).join(' '),
              appointment: order.appointment,
              item: order.item,
            };
            console.log('--- MAPPING ORDER ---');
            console.log('Raw order ID:', order.id);
            console.log('Raw order item:', order.item);
            console.log('Selected item filter:', selectedItem);
            console.log('Item ID match:', order.item?.id === selectedItem?.id);
            console.log('Mapped order:', mapped);
            return mapped;
          });
          
          console.log('--- MAPPED ORDERS ---');
          console.log('Mapped orders:', mappedOrders);
      
          setOrders(mappedOrders);
      
          // Payment summary will be calculated in the useEffect that watches filteredOrders
        })
        .catch((error) => {
          console.error('--- API ERROR ---');
          console.error('Error fetching orders:', error);
        })
        .finally(() => setLoading(false));
    });
  };

  // Fetch hospital info on component mount
  useEffect(() => {
    const fetchHospitalInfo = async () => {
      try {
        const hospital = await hospitalApi.getCompanyProfile();
        setHospitalInfo(hospital);
      } catch (error) {
        console.error('Error fetching hospital info:', error);
      }
    };
    fetchHospitalInfo();
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, [fromDate, toDate, statusFilter, doctorFilter, selectedPatient, selectedItem]);

  // Calculate filtered orders and payment summary
  const filteredOrders = orders.filter(order => {
    // Exclude cancelled and refunded orders from display
    if (order.status === 'cancelled' || order.status === 'refunded') {
      return false;
    }
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter.toLowerCase();
    const matchesDoctor = doctorFilter === 'all' || order.doctorId === doctorFilter;
    const matchesPatient = !selectedPatient || order.patientName === selectedPatient.name;
    const matchesItem = !selectedItem || order.item?.id === selectedItem.id;
    
    return matchesStatus && matchesDoctor && matchesPatient && matchesItem;
  });

  // Recalculate payment summary when filtered orders change
  useEffect(() => {
    if (orders.length > 0) {
      const appointmentIds = [...new Set(filteredOrders.map(order => order.appointment?.id).filter(Boolean))];
      console.log('Recalculating payment summary for filtered appointments:', appointmentIds);
      if (appointmentIds.length > 0) {
        fetchPaymentSummary(appointmentIds);
      } else {
        setPaymentSummary({
          cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0
        });
      }
    }
  }, [orders, statusFilter, doctorFilter, selectedPatient, selectedItem]);

  console.log('--- CURRENT FILTERS ---');
  console.log('Status Filter:', statusFilter);
  console.log('Doctor Filter:', doctorFilter);
  console.log('Selected Patient:', selectedPatient);
  console.log('Selected Item:', selectedItem);
  console.log('Total Orders Before Filtering:', orders.length);
  console.log('--- FILTERING RESULTS ---');
  console.log('Filtered Orders Count:', filteredOrders.length);
  console.log('Filtered Orders:', filteredOrders);

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleDoctorFilterChange = (event: SelectChangeEvent) => {
    setDoctorFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchPaymentSummary = async (appointmentIds: string[]) => {
    try {
      const { appointmentOrdersApi } = await import('../../features/appointments/api/appointmentOrdersApi');
      
      if (appointmentIds.length === 0) {
        setPaymentSummary({
          cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0
        });
        return;
      }
      
      console.log('Fetching payment summaries for appointments:', appointmentIds);
      
      const summaries = await Promise.all(
        appointmentIds.map(async (id) => {
          try {
            const summary = await appointmentOrdersApi.getPaymentCollectionsSummary(id);
            console.log(`Payment summary for appointment ${id}:`, summary);
            return summary;
          } catch (error) {
            console.log(`No payment data for appointment ${id}:`, (error as any)?.message || error);
            // Return empty summary for appointments without payments (404 errors)
            return { cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0 };
          }
        })
      );
      
      // Aggregate all payment summaries
      const totalSummary = summaries.reduce((acc, summary) => {
        const data = summary?.data || summary;
        console.log('Processing payment summary:', data);
        acc.cash += data.cash || 0;
        acc.card += data.card || 0;
        acc.upi += data.upi || 0;
        acc.netbanking += data.netbanking || 0;
        acc.insurance += data.insurance || 0;
        acc.waveOff += data.waveOff || 0;
        acc.other += data.other || 0;
        acc.total += data.total || 0;
        return acc;
      }, {
        cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0
      });
      
      console.log('Final payment summary:', totalSummary);
      setPaymentSummary(totalSummary);
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      setPaymentSummary({
        cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0
      });
    }
  };

  // Calculate totals from filtered orders (excluding cancelled and refunded)
  const activeOrders = filteredOrders.filter(order => 
    order.status !== 'cancelled' && order.status !== 'refunded'
  );
  const totalPatients = new Set(activeOrders.map(order => order.patientName)).size;
  const totalAmount = activeOrders.reduce((sum, order) => sum + order.amount, 0);
  const totalPaid = activeOrders.reduce((sum, order) => sum + order.paidAmount, 0);
  const totalPending = activeOrders.reduce((sum, order) => sum + order.pendingAmount, 0);

  // Calculate item group totals
  const itemGroupTotals = activeOrders.reduce((groups: { [key: string]: { amount: number; count: number } }, order) => {
    const groupName = order.item?.itemGroup || 'OTHERS';
    if (!groups[groupName]) {
      groups[groupName] = { amount: 0, count: 0 };
    }
    groups[groupName].amount += order.amount;
    groups[groupName].count += 1;
    return groups;
  }, {});

  // Define order for item groups
  const itemGroupOrder = ['CONSULTATION', 'THERAPIES', 'PHARMACY', 'CONSUMABLE', 'STATIONERY', 'OTHERS'];
  const sortedItemGroups = Object.keys(itemGroupTotals).sort((a, b) => {
    const indexA = itemGroupOrder.indexOf(a);
    const indexB = itemGroupOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  const handleDownloadPDF = async (includeAllData = false) => {
    if (!reportRef.current) return;
    
    try {
      let originalRowsPerPage = rowsPerPage;
      let originalPage = page;
      
      if (includeAllData) {
        // Temporarily show all data for PDF generation
        setRowsPerPage(filteredOrders.length);
        setPage(0);
        
        // Wait for re-render
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Create PDF with header
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Use hospital info from API or fallback to default
      const pdfHospitalInfo = hospitalInfo || {
        name: "Airyaman Healthcare",
        address: "123 Healthcare Street, Medical District, City - 123456",
        phoneNumber: "+91 12345 67890",
        email: "info@airyaman.com"
      };
      
      // Header section with logo
      try {
        // Load and add logo
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          logoImg.onload = () => {
            // Add logo (16x16mm at 20,10 position)
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            ctx?.drawImage(logoImg, 0, 0);
            const logoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            pdf.addImage(logoDataUrl, 'JPEG', 20, 10, 16, 16);
            resolve(true);
          };
          logoImg.onerror = () => {
            console.warn('Logo not found, continuing without logo');
            resolve(false);
          };
          logoImg.src = '/logo.jpeg';
        });
      } catch (error) {
        console.warn('Error loading logo:', error);
      }
      
      // Hospital name and info (positioned next to logo)
      pdf.setTextColor(0, 0, 0); // Set text color to black
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(pdfHospitalInfo.name, 40, 20);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const address = pdfHospitalInfo.address || 'Address not available';
      const phone = pdfHospitalInfo.phoneNumber || 'Phone not available';
      const email = pdfHospitalInfo.email || 'Email not available';
      
      pdf.text(address, 40, 28);
      pdf.text(`Phone: ${phone}`, 40, 35);
      pdf.text(`Email: ${email}`, 40, 42);
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Financial Statement', 20, 65);
      
      // Date range
      const fromDateStr = fromDate ? fromDate.toLocaleDateString() : 'N/A';
      const toDateStr = toDate ? toDate.toLocaleDateString() : 'N/A';
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${fromDateStr} to ${toDateStr}`, 20, 75);
      
      // Add line separator
      pdf.line(20, 80, 190, 80);
      
      // Create canvas from the report content (excluding filters)
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Restore original pagination if we changed it
      if (includeAllData) {
        setRowsPerPage(originalRowsPerPage);
        setPage(originalPage);
      }
      
      // Add report content to PDF
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit A4 (starting below header)
      const imgWidth = 170; // Reduced width to fit margins
      const startY = 85; // Start below header
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pageHeight = 210; // Remaining height after header
      let heightLeft = imgHeight;
      
      let position = startY;
      
      // Add first page content
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(imgHeight, pageHeight));
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        
        // Add header to subsequent pages
        pdf.setTextColor(0, 0, 0); // Ensure black text on continuation pages
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Financial Statement (Continued)', 20, 20);
        pdf.line(20, 25, 190, 25);
        
        position = heightLeft - imgHeight + 30; // Adjust for new page header
        pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(heightLeft, 240));
        heightLeft -= 240;
      }
      
      // Generate filename with date range
      const fromDateFile = fromDate ? fromDate.toISOString().split('T')[0] : 'unknown';
      const toDateFile = toDate ? toDate.toISOString().split('T')[0] : 'unknown';
      const dataType = includeAllData ? 'Complete' : 'CurrentPage';
      const filename = `FinancialStatement_${dataType}_${fromDateFile}_to_${toDateFile}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Financial Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          View all appointment orders and their financial status
        </Typography>
        <Card sx={{ mb: 3 }}>
  <CardContent>
    <div ref={reportRef}>
    <Grid container spacing={2}>
      {/* First Row: Dates, Status, Doctor */}
      <Grid item xs={12} md={3}>
  <DatePicker
    label="From Date"
    value={fromDate}
    onChange={(date) => setFromDate(date)}
    slotProps={{
      textField: {
        fullWidth: true,
        size: 'small',
        onKeyDown: (e) => e.preventDefault(), // Prevent typing
        onPaste: (e) => e.preventDefault(), // Prevent pasting
      },
    }}
  />
</Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="To Date"
          value={toDate}
          onChange={(date) => setToDate(date)}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              onKeyDown: (e) => e.preventDefault(), // Prevent typing
              onPaste: (e) => e.preventDefault(), // Prevent pasting
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
          >
            {FINANCIAL_STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value.toLowerCase()}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Doctor</InputLabel>
          <Select
            value={doctorFilter}
            label="Doctor"
            onChange={handleDoctorFilterChange}
          >
            <MenuItem value="all">All Doctors</MenuItem>
            {doctors.map((doctor) => (
              <MenuItem key={doctor.id} value={doctor.id}>
                {doctor.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Second Row: Item, Patient, Buttons (right-aligned) */}
      <Grid item xs={12} md={3}>
        <Autocomplete
          options={items}
          value={selectedItem}
          onChange={(_, value) => setSelectedItem(value)}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Item" size="small" fullWidth />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Autocomplete
          options={patients}
          value={selectedPatient}
          onChange={(_, value) => setSelectedPatient(value)}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Patient" size="small" fullWidth />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center">
        <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button
variant="outlined"
color="primary"
startIcon={<DownloadIcon />}
onClick={() => {
// Utility: Convert filteredOrders to CSV and trigger download
const headers = [
'Order ID',
'Patient Name',
'Service Name',
'Amount',
'Paid Amount',
'Pending Amount',
'Order Date',
'Status',
'Doctor Name',
];
const rows = filteredOrders.map(order => [
order.appointmentNumber,
order.patientName,
order.serviceName,
order.amount,
order.paidAmount,
order.pendingAmount,
order.orderDate,
order.status,
order.doctorName,
order.invoiceNumber || ''
]);
const csvContent = [
headers.join(','),
...rows.map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
].join('\r\n');
const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
const url = URL.createObjectURL(blob);
const link = document.createElement('a');
link.href = url;
const today = new Date();
const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
link.setAttribute('download', `FinancialReport_${dateStr}.csv`);
document.body.appendChild(link);
link.click();
document.body.removeChild(link);
URL.revokeObjectURL(url);
}}
>
Export CSV
</Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
            sx={{ height: '40px' }}
          >
            Download PDF
          </Button>
          <Menu
            anchorEl={pdfMenuAnchor}
            open={Boolean(pdfMenuAnchor)}
            onClose={() => setPdfMenuAnchor(null)}
          >
            <MenuItem onClick={() => { handleDownloadPDF(false); setPdfMenuAnchor(null); }}>
              <ListItemIcon>
                <PictureAsPdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Current Page Only" />
            </MenuItem>
            <MenuItem onClick={() => { handleDownloadPDF(true); setPdfMenuAnchor(null); }}>
              <ListItemIcon>
                <PictureAsPdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Complete Report" />
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            sx={{ height: '40px' }}
            onClick={fetchOrders}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ height: '40px' }}
            onClick={() => {
              const now = new Date();
              // Use local timezone
              const localStart = new Date(now);
              localStart.setHours(0, 0, 0, 0);
              const localEnd = new Date(now);
              localEnd.setHours(23, 59, 59, 999);
              setFromDate(localStart);
              setToDate(localEnd);
              setStatusFilter('all');
              setDoctorFilter('all');
              setSelectedPatient(null);
              setSelectedItem(null);
            }}
          >
            Clear Filters
          </Button>
          </Stack>

</Grid>
{/* Export CSV Button */}

</Grid>

<TableContainer><Table><TableHead><TableRow>
                <TableCell>Order Number</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Service</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="right">Paid Amount</TableCell>
                <TableCell align="right">Pending Amount</TableCell>
                <TableCell align="right">Line Total</TableCell>
                <TableCell>Doctor</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length > 0 ? (
                filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((order, index) => (
                    <TableRow key={`${order.id}-${index}`}>
                      <TableCell>{order.appointmentNumber}</TableCell>
                      <TableCell>
                        {formatDateForDisplay(order.orderDate)}
                        {order.appointment?.appointmentDate && order.orderDate !== order.appointment.appointmentDate && (
                          <span style={{ color: '#666', fontSize: '0.85em' }}>
                            <br />({formatDateForDisplay(order.appointment.appointmentDate)})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{order.patientName}</TableCell>
                      <TableCell>{order.serviceName}</TableCell>
                      <TableCell align="right">{order.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      <TableCell align="right">{order.paidAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      <TableCell align="right">{order.pendingAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      <TableCell align="right">{order.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                      <TableCell>{order.doctorName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status} 
                          size="small" 
                          sx={{ color: 'black !important' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No orders found
                  </TableCell>
                </TableRow>
              )}
              {/* Summary Row */}
              {filteredOrders.length > 0 && (
                <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell sx={{ fontWeight: 'bold' }}>{filteredOrders.length} Orders</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{filteredOrders.reduce((sum, order) => sum + order.amount, 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{filteredOrders.reduce((sum, order) => sum + order.paidAmount, 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{filteredOrders.reduce((sum, order) => sum + order.pendingAmount, 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{filteredOrders.reduce((sum, order) => sum + order.amount, 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination Controls - Moved here for better UX */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 500]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        
        {/* Summary Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    {totalPatients}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Patients
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    ₹{totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    ₹{totalPaid.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Paid
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="error.main">
                    ₹{totalPending.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="info.main">
                    ₹{(paymentSummary?.total || totalPaid).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actual Collection
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    (Excludes Wave Off: ₹{(paymentSummary?.waveOff || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Payment Mode Wise Split */}
          {paymentSummary && paymentSummary.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payment Mode Wise Collection
              </Typography>
              <Grid container spacing={2}>
                {paymentSummary.cash > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="success.main">
                          ₹{paymentSummary.cash.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cash
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {paymentSummary.card > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="info.main">
                          ₹{paymentSummary.card.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Card
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {paymentSummary.upi > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary.main">
                          ₹{paymentSummary.upi.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          UPI
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {paymentSummary.netbanking > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="secondary.main">
                          ₹{paymentSummary.netbanking.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Net Banking
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {paymentSummary.insurance > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="warning.main">
                          ₹{paymentSummary.insurance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Insurance
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {paymentSummary.waveOff > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="error.main">
                          ₹{paymentSummary.waveOff.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Wave Off
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {paymentSummary.other > 0 && (
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="text.primary">
                          ₹{paymentSummary.other.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Other
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          
          {/* Item Group Totals */}
          {sortedItemGroups.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Service Category Breakdown
              </Typography>
              <Grid container spacing={2}>
                {sortedItemGroups.map((groupName) => {
                  const groupData = itemGroupTotals[groupName];
                  const getGroupColor = (group: string) => {
                    switch (group) {
                      case 'CONSULTATION': return 'primary.main';
                      case 'THERAPIES': return 'secondary.main';
                      case 'PHARMACY': return 'success.main';
                      case 'CONSUMABLE': return 'warning.main';
                      case 'STATIONERY': return 'info.main';
                      default: return 'text.primary';
                    }
                  };
                  
                  return (
                    <Grid item xs={6} md={2} key={groupName}>
                      <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" color={getGroupColor(groupName)}>
                            ₹{groupData.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {groupName.charAt(0) + groupName.slice(1).toLowerCase()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({groupData.count} order{groupData.count !== 1 ? 's' : ''})
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
          
          {/* Doctor Wise Summary */}
          {filteredOrders.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Doctor Wise Summary
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  // Calculate doctor wise totals
                  const doctorTotals = filteredOrders.reduce((acc, order) => {
                    const doctorName = order.doctorName || 'Unknown Doctor';
                    if (!acc[doctorName]) {
                      acc[doctorName] = {
                        totalAmount: 0,
                        paidAmount: 0,
                        pendingAmount: 0,
                        orderCount: 0,
                        patientCount: new Set()
                      };
                    }
                    acc[doctorName].totalAmount += order.amount;
                    acc[doctorName].paidAmount += order.paidAmount;
                    acc[doctorName].pendingAmount += order.pendingAmount;
                    acc[doctorName].orderCount += 1;
                    acc[doctorName].patientCount.add(order.patientName);
                    return acc;
                  }, {} as Record<string, {
                    totalAmount: number;
                    paidAmount: number;
                    pendingAmount: number;
                    orderCount: number;
                    patientCount: Set<string>;
                  }>);

                  // Sort doctors by total amount (descending)
                  const sortedDoctors = Object.entries(doctorTotals)
                    .sort(([,a], [,b]) => b.totalAmount - a.totalAmount);

                  return sortedDoctors.map(([doctorName, totals]) => (
                    <Grid item xs={12} md={6} lg={4} key={doctorName}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {doctorName}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Total Amount:
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight="bold">
                              ₹{totals.totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Paid Amount:
                            </Typography>
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              ₹{totals.paidAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Pending Amount:
                            </Typography>
                            <Typography variant="body2" color="error.main" fontWeight="bold">
                              ₹{totals.pendingAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Orders:
                            </Typography>
                            <Typography variant="body2">
                              {totals.orderCount}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Patients:
                            </Typography>
                            <Typography variant="body2">
                              {totals.patientCount.size}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ));
                })()}
              </Grid>
            </Box>
          )}
        </Box>
        </div>
        </CardContent>
        </Card>
        </Box>
        </LocalizationProvider>
  );
}

export default FinancialReport;